import { useState, useRef } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, SaveIcon } from 'lucide-react'
import { useProductVariantApi } from 'src/api/db/apiProductVariant'
import { useProductsClosedApi } from 'src/api/db/apiProductsClosed'
import { useQuoteItemApi } from 'src/api/db/apiQuoteItem'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { PageContainer } from 'src/shared/components/PageContainer'
import { TabNavigation } from 'src/shared/components/TabNavigation'
import { Button } from 'src/shared/components/ui/button'
import { useDraftProduct } from './hooks'
import { TABS, DEFAULT_DRAFT_PRODUCT } from './consts'
import { DraftStatusBadge } from './components/DraftStatusBadge'
import { TabContent } from './components/TabContent'
import { adminOrdersPath, paths } from 'src/routes/paths'
import { useToast } from 'src/shared/hooks/use-toast'

export const AddEditProductPage = () => {
  const { productId: productIdParam } = useParams<{ productId?: string }>()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isMyAccount = pathname.startsWith(paths.myAccountInventory)
  const productBasePath = isMyAccount
    ? paths.myAccountInventory
    : paths.adminProducts
  const [searchParams] = useSearchParams()
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState<string>('product')
  const saveHandlerRef = useRef<(() => void) | null>(null)
  const [hasChanges, setHasChanges] = useState<boolean>(false)
  const productToUpdate =
    productIdParam && productIdParam !== 'new'
      ? parseInt(productIdParam, 10)
      : undefined

  // Get quote_id from query params
  const quoteIdParam = searchParams.get('quote_id')

  const { productsClosed, createProduct, updateProduct } = useProductsClosedApi(
    {
      product_id: productToUpdate,
    }
  )
  const productId = useDraftProduct(productToUpdate, productsClosed)
  const { createProductVariant, productVariants } = useProductVariantApi({
    product_id: productId,
  })
  const { createQuoteItem } = useQuoteItemApi({})

  const product = productsClosed?.data?.[0]

  const handleToggleDraft = async () => {
    if (productId) {
      await updateProduct.mutateAsync({
        id: productId,
        is_draft: !product?.is_draft,
      })
    }
  }

  const handleCreateDraft = async () => {
    const res = await createProduct.mutateAsync(DEFAULT_DRAFT_PRODUCT)
    const newProductId = res.data?.id ?? 0

    // Create product variant
    await createProductVariant.mutateAsync({
      product_id: newProductId,
    })

    // Navigate to product edit page, preserving quote_id if it exists
    if (quoteIdParam) {
      navigate(`${productBasePath}/${newProductId}?quote_id=${quoteIdParam}`)
    } else {
      navigate(`${productBasePath}/${newProductId}`)
    }
  }

  const handleSaveChanges = async () => {
    if (saveHandlerRef.current) {
      try {
        // Save the product changes
        await saveHandlerRef.current()

        // Publish the product (set is_draft to false)
        if (productId && product?.is_draft) {
          await updateProduct.mutateAsync({
            id: productId,
            is_draft: false,
          })
        }

        // Reset changes after save (tabs will update their own state)
        setHasChanges(false)

        // If we have quote_id, add product to quote and navigate back
        if (quoteIdParam && productId) {
          try {
            const quoteId = parseInt(quoteIdParam, 10)

            // Get the first product variant color ID if available
            // Try to get from productVariants first (more reliable after save)
            const productVariant = productVariants?.data?.[0]
            const productVariantColor =
              productVariant?.product_variant_color?.[0]
            let productVariantColorId = productVariantColor?.id || null

            // Fallback to product data if productVariants not available
            if (!productVariantColorId) {
              const productVariantFromProduct = (product as any)
                ?.product_variant?.[0]
              const productVariantColorFromProduct =
                productVariantFromProduct?.product_variant_color?.[0]
              productVariantColorId = productVariantColorFromProduct?.id || null
            }

            await createQuoteItem.mutateAsync({
              quote_id: quoteId,
              product_id: productId,
              product_variant_color_id: productVariantColorId,
              quantity: 1,
            })

            toast({
              title: 'Product saved and added to quote',
              description:
                'The product has been saved and added to your quote.',
              variant: 'success',
            })

            // Navigate back to quote page
            navigate(`${adminOrdersPath}/${quoteId}`)
          } catch (error) {
            console.error('Error adding product to quote:', error)
            // Don't navigate away if adding to quote fails - let user continue editing
            toast({
              title: 'Product saved',
              description: 'Product was saved but could not be added to quote.',
              variant: 'default',
            })
          }
        }
      } catch (error) {
        console.error('Error saving product:', error)
        // Error handling is done by the individual tab save handlers
      }
    }
  }

  const handleHasChangesChange = (hasChanges: boolean) => {
    setHasChanges(hasChanges)
  }

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab)
    // Reset changes when switching tabs - the new tab will report its state
    setHasChanges(false)
  }

  return (
    <PageContainer>
      <Row className="items-center gap-4 mb-6">
        <Button
          onClick={() => {
            if (quoteIdParam) {
              navigate(`${adminOrdersPath}/${quoteIdParam}`)
            } else {
              window.history.back()
            }
          }}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {quoteIdParam ? 'Back to quote' : 'Back to products'}
        </Button>

        <Row className="gap-2 items-center">
          <h2>Add/edit product</h2>
          {productId && (
            <DraftStatusBadge product={product} onToggle={handleToggleDraft} />
          )}
        </Row>

        <Row className="items-center gap-2 ml-auto">
          {!productId && (
            <Button
              isLoading={createProduct.isPending}
              onClick={handleCreateDraft}
            >
              Create draft product
            </Button>
          )}

          {productId && (
            <Button
              variant="primary"
              onClick={handleSaveChanges}
              disabled={!hasChanges}
            >
              <SaveIcon className="h-4 w-4 mr-2" /> Save changes
            </Button>
          )}
        </Row>
      </Row>

      {productId && (
        <>
          <TabNavigation
            tabs={TABS}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          <Column className="gap-4 mt-6">
            <TabContent
              activeTab={activeTab}
              productId={productId}
              onSaveHandlerReady={(handler) => {
                saveHandlerRef.current = handler
              }}
              onHasChangesChange={handleHasChangesChange}
            />
          </Column>
        </>
      )}
    </PageContainer>
  )
}
